import React, { useState, useEffect } from "react";
import * as SubframeCore from "@subframe/core";
import { Escalation_Badge } from "subframe/components/Escalation_Badge";
import { Accordion } from "subframe/components/Accordion";
import { Button } from "subframe/components/Button";
import { fetchSurveyQuestions } from "modules/Retention/services/retention";
import { useQuery } from "@tanstack/react-query";
import endpoints from "modules/Retention/constants/endpoints";
import { SurveyQuestion } from "./SurveyQuestion";
import { useNavigate } from "react-router-dom";
import { TagScoreTxt } from "subframe/components/TagScoreTxt";
import { formatString } from "modules/Retention/utils/recognition";
import { TagEscalationStatus } from "subframe/components/TagEscalationStatus";
import { EscalationReason } from "modules/Retention/types";
import { formatLocalDate } from "utils/helper";

interface SurveyQuestion {
  flagged: boolean;
  category: string;
  question: string;
  response: string;
  sentiment: string | null;
  question_type: string;
}

interface SurveyHistoryItem {
  survey_id: string;
  employee_id: string;
  escalation_id: string | null;
  title: string;
  date: string;
  score: string;
  outcome: string | null;
  notes: string | null;
  notes_employee_name: string | null;
  note_tags: string[] | null;
  reason: string | null;
  questions: any[];
  sentiment_analysis: {
    positive_count: number;
    negative_count: number;
    neutral_count: number;
    total_count: number;
    sentiment_score: number;
    overall_sentiment: string;
  };
}

interface SurveyHistoryItemsProps {
  surveyHistory?: SurveyHistoryItem[];
  isLoading?: boolean;
  employeeId?: number;
  outcomeFilter?: string;
}

export const SurveyHistoryItems: React.FC<SurveyHistoryItemsProps> = ({
  surveyHistory = [],
  isLoading = false,
  employeeId,
  outcomeFilter = "All"
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const handleExpandAll = () => {
    const allItems = surveyHistory.map(item => item.survey_id);
    setExpandedItems(new Set(allItems));
  };

  const handleCollapseAll = () => {
    setExpandedItems(new Set());
  };

  const toggleItem = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const isAllExpanded = expandedItems.size === surveyHistory.length;

  // Listen for expand all event from parent
  useEffect(() => {
    const handleExpandAllEvent = () => {
      if (isAllExpanded) {
        handleCollapseAll();
      } else {
        handleExpandAll();
      }
    };

    window.addEventListener('expandAllSurveys', handleExpandAllEvent);
    return () => {
      window.removeEventListener('expandAllSurveys', handleExpandAllEvent);
    };
  }, [isAllExpanded, surveyHistory.length]);

  // Filter survey history based on outcome
  const filteredSurveyHistory = surveyHistory.filter((survey) => {
    if (outcomeFilter === "All") {
      return true;
    }
    return survey.outcome === outcomeFilter;
  });

  if (isLoading) {
    return (
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">Loading survey history...</span>
        </div>
      </div>
    );
  }

  if (!surveyHistory || surveyHistory.length === 0) {
    return (
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">No survey history available</span>
        </div>
      </div>
    );
  }

  if (filteredSurveyHistory.length === 0) {
    return (
      <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">No surveys found with the selected outcome</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
      {[...filteredSurveyHistory]
        .sort((a, b) => {
          // First, prioritize surveys with NEEDS_ATTENTION outcome
          const aNeedsAttention = a.outcome === "NEEDS_ATTENTION";
          const bNeedsAttention = b.outcome === "NEEDS_ATTENTION";

          if (aNeedsAttention && !bNeedsAttention) return -1;
          if (!aNeedsAttention && bNeedsAttention) return 1;

          // Second, prioritize surveys with escalations
          const aHasEscalation = !!a.escalation_id;
          const bHasEscalation = !!b.escalation_id;

          if (aHasEscalation && !bHasEscalation) return -1;
          if (!aHasEscalation && bHasEscalation) return 1;

          // If both have same priority status, sort by date descending
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        })
        .map((survey) => (
          <SurveyHistoryItem
            key={survey.survey_id}
            survey={survey}
            employeeId={employeeId}
            isExpanded={expandedItems.has(survey.survey_id)}
            onToggle={() => toggleItem(survey.survey_id)}
          />
        ))}
    </div>
  );
};

interface SurveyHistoryItemProps {
  survey: SurveyHistoryItem;
  employeeId?: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const SurveyHistoryItem: React.FC<SurveyHistoryItemProps> = ({
  survey,
  employeeId,
  isExpanded,
  onToggle
}) => {
  const navigate = useNavigate();

  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: [endpoints.surveyQuestions, employeeId, survey.survey_id],
    queryFn: () => fetchSurveyQuestions({
      employee_id: employeeId,
      survey_id: survey.survey_id
    }),
    select: (res) => res?.result,
    enabled: !!employeeId && !!survey.survey_id && isExpanded,
    staleTime: 180000, // 3 minutes cache
  });


  // Show flagged questions & tag if the survey has an escalation and the reason is LOW_SENTIMENT_SCORE_FLAGGED_QUESTION
  const showFlagged = Boolean(survey.escalation_id && survey.reason === EscalationReason.LOW_SENTIMENT_SCORE_FLAGGED_QUESTION);

  const getEscalationVariant = (outcome: string | null) => {
    if (!outcome) return "dismissed";

    // RESOLVED, DISMISSED, IN_PROGRESS, EXPIRED, AT_RISK
    switch (outcome) {
      case "NEEDS_ATTENTION":
        return "alert";
      case "RESOLVED":
        return "success";
      case "DISMISSED":
        return "dismissed";
      case "EXPIRED":
        return "dismissed";
      case "IN_PROGRESS":
        return "pending";
      case "AT_RISK":
        return "alert";
      default:
        return "dismissed";
    }
  };

  const getScoreColor = (score: string) => {
    switch (score?.toLowerCase()) {
      case "high":
        return "success";
      case "medium":
        return "medium";
      case "low":
        return "alert";
      default:
        return "default";
    }
  };

  const handleViewEscalationTicket = () => {
    if (survey.escalation_id && survey.survey_id) {
      navigate(`/retention/v3/employees/${employeeId}/survey/${survey.survey_id}/escalation/${survey.escalation_id}`);
    }
  };

  return (
    <div className="flex w-full flex-col items-start justify-end gap-1 rounded-lg border border-solid border-neutral-border bg-default-background">
      <Accordion
        open={isExpanded}
        onOpenChange={onToggle}
        defaultValue={survey.survey_id}
        className={survey.score === "No response" ? "pointer-events-none cursor-not-allowed" : ""}
        trigger={
          <div className="flex w-full items-center gap-2 sm:gap-4 rounded-lg border border-solid border-neutral-border px-3 sm:px-4 lg:px-6 py-2">
            <div className="flex grow shrink-0 basis-0 items-center justify-between">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 lg:gap-12">
                <div className="flex w-24 sm:w-32 lg:w-36 flex-none flex-col items-start gap-1">
                  <span className="text-subheader font-subheader text-neutral-700">
                    {survey.title}
                  </span>
                  <span className="font-['Nunito'] text-[11px] sm:text-[12px] font-[400] leading-[14px] sm:leading-[16px] text-neutral-500">
                    {formatLocalDate(survey.date)}
                  </span>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <TagScoreTxt
                    score={survey.score}
                    variant={getScoreColor(survey.score)}
                  />
                  {showFlagged && questionsData?.questions && (
                    (() => {
                      const flaggedCount = questionsData.questions.filter((q: SurveyQuestion) => q.flagged).length;
                      return flaggedCount > 0 ? (
                        <span className="font-['Nunito'] text-[11px] sm:text-[12px] font-[400] leading-[14px] sm:leading-[16px] text-error-700">
                          {flaggedCount} flagged question{flaggedCount > 1 ? 's' : ''}
                        </span>
                      ) : null;
                    })()
                  )}
                  {!questionsData?.questions && survey.sentiment_analysis?.total_count > 0 && (
                    <span className="font-['Nunito'] text-[11px] sm:text-[12px] font-[400] leading-[14px] sm:leading-[16px] text-subtext-color">
                      {survey.sentiment_analysis.total_count} question{survey.sentiment_analysis.total_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              {survey.outcome &&
                <TagEscalationStatus variant={getEscalationVariant(survey.outcome)} score={formatString(survey.outcome)} />
              }
            </div>
            <Accordion.Chevron />
          </div>
        }
      >
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-3 sm:gap-4 border-t border-solid border-neutral-border bg-neutral-50 px-3 sm:px-4 py-3 sm:py-4">
          {questionsLoading ? (
            <div className="flex w-full items-center justify-center py-4">
              <span className="text-body font-body text-subtext-color">Loading survey details...</span>
            </div>
          ) : questionsData?.questions && questionsData.questions.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
              {questionsData.questions
                .sort((a: SurveyQuestion, b: SurveyQuestion) => {
                  // Sort questions with sentiment first, then those without
                  const aHasSentiment = a.sentiment && a.sentiment.toLowerCase() !== 'neutral';
                  const bHasSentiment = b.sentiment && b.sentiment.toLowerCase() !== 'neutral';

                  if (aHasSentiment && !bHasSentiment) return -1;
                  if (!aHasSentiment && bHasSentiment) return 1;
                  return 0;
                })
                .map((question: SurveyQuestion, index: number) => (
                  <SurveyQuestion
                    key={index}
                    question={question.question}
                    score={question.response}
                    sentiment={question.sentiment?.toLowerCase() || ""}
                    category={question.category}
                    flagged={question.flagged}
                    showFlagged={showFlagged}
                    type={question.question_type}
                  />
                ))}
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
              <div className="flex w-full flex-col items-start gap-2 px-1 py-1">
                <span className="w-full text-body font-body text-default-font">
                  Survey Details
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  {survey.notes || "No additional details available"}
                </span>
              </div>
            </div>
          )}

          {survey.outcome !== "AT_RISK" && (
            <div className="flex w-full flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 rounded-lg bg-default-background px-3 sm:px-4 py-3 sm:py-4 shadow-sm">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                <div className="flex items-center gap-2">
                  <SubframeCore.Icon
                    className="text-body font-body text-default-font"
                    name="FeatherUser"
                  />
                  <span className="text-body-bold font-body-bold text-neutral-700">
                    {survey.notes_employee_name ?? 'Admin'}
                  </span>
                </div>
                {survey.note_tags && survey.note_tags.length > 0 && (
                  <div className="flex flex-wrap items-start gap-2">
                    {survey.note_tags.map((tag: string) => (
                      <Escalation_Badge
                        variant="variation"
                        score={tag}
                      />
                    ))}
                  </div>
                )}
                {survey.notes && (
                  <span className="text-label font-label text-default-font">
                    {survey.notes}
                  </span>
                )}
              </div>
            </div>
          )}

          {survey.escalation_id && (
            <Button
              className="w-full sm:w-auto"
              onClick={handleViewEscalationTicket}
            >
              View Escalation Ticket
            </Button>
          )}
        </div>
      </Accordion>
    </div>
  );
};